﻿export function initViwoFileDrops(root = document) {
    const drops = Array.from(root.querySelectorAll("[data-viwo-file-drop]"));

    drops.forEach(drop => {
        if (drop.dataset.viwoFileDropInitialized === "true") return;

        drop.dataset.viwoFileDropInitialized = "true";

        const input = drop.querySelector("input[type='file']");
        const fileName = drop.querySelector("[data-viwo-file-name]");
        const removeBtn = drop.querySelector("[data-viwo-file-remove]");
        const icon = drop.querySelector(".viwo-file-drop__icon");

        if (!input) return;

        drop.dataset.defaultIconHtml = icon?.innerHTML || `<i class="fa-regular fa-cloud-arrow-up"></i>`;

        syncFileDropState(drop, input, fileName, icon);

        input.addEventListener("change", () => {
            syncFileDropState(drop, input, fileName, icon);
        });

        removeBtn?.addEventListener("click", event => {
            event.preventDefault();
            event.stopPropagation();

            clearFileInput(input);

            input.dispatchEvent(new Event("change", {
                bubbles: true
            }));

            syncFileDropState(drop, input, fileName, icon);
        });

        drop.addEventListener("dragenter", event => {
            event.preventDefault();
            event.stopPropagation();
            drop.classList.add("is-dragging");
        });

        drop.addEventListener("dragover", event => {
            event.preventDefault();
            event.stopPropagation();
            drop.classList.add("is-dragging");
        });

        drop.addEventListener("dragleave", event => {
            event.preventDefault();
            event.stopPropagation();

            if (!drop.contains(event.relatedTarget)) {
                drop.classList.remove("is-dragging");
            }
        });

        drop.addEventListener("drop", event => {
            event.preventDefault();
            event.stopPropagation();

            drop.classList.remove("is-dragging");

            const files = event.dataTransfer?.files;
            if (!files || files.length === 0) return;

            input.files = files;

            input.dispatchEvent(new Event("change", {
                bubbles: true
            }));

            syncFileDropState(drop, input, fileName, icon);
        });
    });
}

function clearFileInput(input) {
    input.value = "";

    if (input.value) {
        const parent = input.parentNode;
        const next = input.nextSibling;
        const form = document.createElement("form");

        form.appendChild(input);
        form.reset();

        if (next) {
            parent.insertBefore(input, next);
        } else {
            parent.appendChild(input);
        }
    }
}

function syncFileDropState(drop, input, fileName, icon) {
    const files = Array.from(input.files || []);

    drop.classList.toggle("has-file", files.length > 0);
    drop.classList.toggle("has-image-preview", false);

    updateFileDropIcon(drop, icon, files);

    if (!fileName) return;

    if (!files.length) {
        fileName.textContent = drop.dataset.emptyText || "No file selected";
        return;
    }

    if (files.length === 1) {
        const file = files[0];
        fileName.textContent = `${file.name} · ${formatFileSize(file.size)}`;
        return;
    }

    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    fileName.textContent = `${files.length} files · ${formatFileSize(totalSize)}`;
}

function updateFileDropIcon(drop, icon, files) {
    if (!icon) return;

    revokeOldPreviewUrl(drop);

    if (!files.length) {
        icon.innerHTML = drop.dataset.defaultIconHtml || `<i class="fa-regular fa-cloud-arrow-up"></i>`;
        icon.removeAttribute("data-file-kind");
        return;
    }

    if (files.length > 1) {
        icon.innerHTML = `<i class="fa-regular fa-files"></i>`;
        icon.setAttribute("data-file-kind", "multiple");
        return;
    }

    const file = files[0];

    if (isImageFile(file)) {
        const url = URL.createObjectURL(file);
        drop.dataset.previewUrl = url;
        drop.classList.add("has-image-preview");

        icon.innerHTML = `
            <img class="viwo-file-drop__thumb"
                 src="${url}"
                 alt=""
                 loading="lazy" />
        `;

        icon.setAttribute("data-file-kind", "image");
        return;
    }

    const meta = getFileVisualMeta(file);

    icon.innerHTML = `<i class="${meta.icon}"></i>`;
    icon.setAttribute("data-file-kind", meta.kind);
}

function revokeOldPreviewUrl(drop) {
    const oldUrl = drop.dataset.previewUrl;

    if (!oldUrl) return;

    URL.revokeObjectURL(oldUrl);
    delete drop.dataset.previewUrl;
}

function isImageFile(file) {
    if (!file) return false;

    if (file.type?.startsWith("image/")) {
        return true;
    }

    const ext = getExtension(file.name);

    return ["jpg", "jpeg", "png", "webp", "gif", "svg", "avif"].includes(ext);
}

function getFileVisualMeta(file) {
    const ext = getExtension(file.name);
    const type = String(file.type || "").toLowerCase();

    if (type.includes("pdf") || ext === "pdf") {
        return {
            kind: "pdf",
            icon: "fa-regular fa-file-pdf"
        };
    }

    if (type.includes("csv") || ext === "csv") {
        return {
            kind: "csv",
            icon: "fa-regular fa-file-csv"
        };
    }

    if (["xls", "xlsx"].includes(ext)) {
        return {
            kind: "excel",
            icon: "fa-regular fa-file-excel"
        };
    }

    if (["doc", "docx"].includes(ext)) {
        return {
            kind: "word",
            icon: "fa-regular fa-file-word"
        };
    }

    if (["ppt", "pptx"].includes(ext)) {
        return {
            kind: "powerpoint",
            icon: "fa-regular fa-file-powerpoint"
        };
    }

    if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) {
        return {
            kind: "archive",
            icon: "fa-regular fa-file-zipper"
        };
    }

    if (["json", "xml", "html", "css", "js", "ts", "sql", "cs", "vb"].includes(ext)) {
        return {
            kind: "code",
            icon: "fa-regular fa-file-code"
        };
    }

    if (["txt", "md", "log"].includes(ext)) {
        return {
            kind: "text",
            icon: "fa-regular fa-file-lines"
        };
    }

    if (["mp4", "mov", "avi", "webm", "mkv"].includes(ext)) {
        return {
            kind: "video",
            icon: "fa-regular fa-file-video"
        };
    }

    if (["mp3", "wav", "ogg", "m4a"].includes(ext)) {
        return {
            kind: "audio",
            icon: "fa-regular fa-file-audio"
        };
    }

    return {
        kind: "file",
        icon: "fa-regular fa-file"
    };
}

function getExtension(fileName = "") {
    const clean = String(fileName).toLowerCase();
    const index = clean.lastIndexOf(".");

    if (index < 0) return "";

    return clean.slice(index + 1);
}

function formatFileSize(bytes) {
    if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB";

    const units = ["B", "KB", "MB", "GB"];
    let size = bytes;
    let index = 0;

    while (size >= 1024 && index < units.length - 1) {
        size /= 1024;
        index++;
    }

    return `${size.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}